import OrderEstimateStatus from "../../components/orders/OrderEstimateStatus";
import { useEffect, useRef, useState } from "react";
import { OrderDetailsViewModel } from "../../viewmodels/orders/OrderDetailsViewModel";

import { Link, useParams } from "react-router-dom";
import { shippingApi } from "../../shared/shippingApi";

import { connect } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";

import * as actions from "../../store/actions/index";

import { withRouter } from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import OrderDetailsForm from "../../components/forms/orders/OrderDetailsForm";
import type { OrderDetailsFormHandle } from "../../components/forms/orders/OrderDetailsForm";
import type { AuthState } from "../../types/auth";
import type { CartState } from "../../types/orders";
import type { OrderShipmentSummary, ShipmentStatus } from "../../types/shipping";
import { getSalesOrderTitle } from "../../shared/orderNumbers";

interface OrderDetailsProps { logout: () => void; quickbooksConnected: boolean }
interface StoreState { auth: AuthState; cart: CartState }
const shipmentLabels: Record<ShipmentStatus, string> = {
  1: "IN PROGRESS", 4: "SHIPPED", 5: "READY FOR PICKUP", 6: "COMPLETED", 7: "VOID",
};

function OrderDetails(props: OrderDetailsProps) {
  const { logout, quickbooksConnected } = props;

  const { orderId } = useParams();
  const [shipments, setShipments] = useState<OrderShipmentSummary[]>([]);
  const [shipmentError, setShipmentError] = useState("");

  const {
    customers,
    getCustomersForDropdown,
    getMyProducts,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    updateOrderApiCall,
    order,
    getOrderDetails,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    employeeOptions,
    getUsersForDropdown,
    printOrder,
    downloadEstimate,
    downloadingEstimate,
  } = OrderDetailsViewModel(orderId, logout);

  const formRef = useRef<OrderDetailsFormHandle>(null);

  const apiFunctionCalls = async function () {
    void getOrderDetails();
    void getCustomersForDropdown();
    void getUsersForDropdown();
    if (!orderId) return;
    const shipmentResult = await shippingApi.orderShipments(orderId);
    if (shipmentResult.success) setShipments(shipmentResult.data.shipments || []);
    else setShipmentError(shipmentResult.data?.message || "Could not load this order's shipments.");
  };

  useEffect(() => {
    apiFunctionCalls();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="max-w-full m-6 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-full mx-auto p-6">
          <div className="px-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {order ? getSalesOrderTitle(order) : "Order Details"}
                </h1>
              </div>

              {/* Action Buttons */}
              {order && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => formRef.current?.submit()}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  >
                    Save
                  </button>
                  {printOrder && (
                    <button
                      onClick={printOrder}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                        />
                      </svg>
                      Print
                    </button>
                  )}
                  {Number(order.quickbooks_estimate_id ?? 0) > 0 ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center disabled:cursor-wait disabled:opacity-60 rounded-md bg-green-600 px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={downloadEstimate}
                      disabled={downloadingEstimate}
                      aria-busy={downloadingEstimate}
                    >
                      {downloadingEstimate && (
                        <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {downloadingEstimate ? "Downloading…" : "Download QB Estimate"}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {orderId && order && <div className="mx-6 mb-4">
        <OrderEstimateStatus key={orderId} orderId={orderId} connected={quickbooksConnected}
          hasEstimate={Boolean(order.quickbooks_estimate_id)} onCreated={() => void getOrderDetails()} />
      </div>}

      {/* Main Content */}
      <div className="max-w-full mx-auto px-6 pt-3 pb-6">
        {order ? (
          <div className="gap-6">
            {/* Main Content Column */}

            <div>
              <OrderDetailsForm
                ref={formRef}
                customers={customers}
                getMyProducts={getMyProducts}
                isEdit={true}
                order={order}
                setEmptyModelOpen={setEmptyModelOpen}
                updateOrderApiCall={updateOrderApiCall}
                onUpdateOrderItem={onUpdateOrderItem}
                deleteOrderItemApiCall={deleteOrderItemApiCall}
                addProductToOrderApiCall={addProductToOrderApiCall}
                employeeOptions={employeeOptions}
              />
            </div>
            <section className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Shipments for this order</h2>
                  <p className="text-sm text-gray-500">
                    Each partial shipment remains an individual outbound record.
                  </p>
                </div>
                <Link
                  to={`/create-shipping?orderId=${orderId}`}
                  className="rounded-lg bg-main-purple px-4 py-2 text-center text-sm font-semibold text-white hover:bg-indigo-600"
                >
                  Create shipment
                </Link>
              </div>
              {shipmentError && (
                <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">
                  {shipmentError}
                </p>
              )}
              <div className="mt-4 divide-y divide-gray-100">
                {shipments.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500">
                    No shipments have been created for this order.
                  </p>
                ) : (
                  shipments.map((shipment) => (
                    <div
                      key={shipment.id}
                      className="grid gap-3 py-4 sm:grid-cols-[1fr_1fr_auto] sm:items-center"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">Shipment #{shipment.id}</p>
                        <p className="text-sm text-gray-500">
                          {shipment.invoice_number
                            ? `Invoice ${shipment.invoice_number}`
                            : "No invoice number"}
                          {shipment.seal_number ? ` · Seal ${shipment.seal_number}` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-sm tabular-nums text-gray-900">
                          {Number(shipment.total_quantity || 0).toLocaleString(undefined, {
                            maximumFractionDigits: 4,
                          })}{" "}
                          shipped
                        </p>
                        <p className="text-xs text-gray-500">
                          {shipment.line_count} lines · {shipment.created_at}
                        </p>
                      </div>
                      <div className="flex items-center justify-end gap-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${Number(shipment.status) === 7 ? "bg-red-100 text-red-700" : Number(shipment.status) === 1 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}
                        >
                          {shipmentLabels[shipment.status]}
                        </span>
                        <Link
                          to={`/shippings/${shipment.id}`}
                          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          {Number(shipment.status) === 7 ? "View" : "Edit"}
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        ) : (
          <div className="px-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Loading State */}
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
                  <p className="text-gray-500">Loading order details...</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={() => setEmptyModelOpen({ childComponent: null, open: false })}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state: StoreState) => {
  return { quickbooksConnected: state.auth.quickbooksConnected };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StoreState, unknown, AnyAction>) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrderDetails));
